"use client";

import {
  RUNTIME_OFFER_PLACEMENT_HI_SHADBALA_REPORT_BRIDGE,
  RUNTIME_OFFER_PLACEMENT_SHADBALA_REPORT_BRIDGE,
} from "@/lib/offer-placement-runtime-keys.generated";

import { useRef, useState, type RefObject } from "react";
import type { KundaliRequest } from "@ishvaram/api-client";

import BirthDetailsForm from "@/components/BirthDetailsForm";
import { OfferPlacement } from "@/components/OfferPlacement";
import { pageReportBridgeOffer } from "@/components/OfferPlacement.page-bridge";
import { trackCalculatorUsed } from "@/lib/analytics-free-tools";
import {
  calculateShadbala,
  type PlanetShadbalaRow,
  type ShadbalaRequest,
  type ShadbalaResponse,
} from "@/lib/api/shadbala";

type Language = "en" | "hi";

const COPY = {
  en: {
    heading: "Planetary Strength Preview — 4 Computed Shadbala Factors",
    submit: "Calculate planetary strength",
    summary: "Computed strength preview",
    strongest: "Highest computed score",
    lowest: "Lowest computed score",
    methodology:
      "This is a transparent 4-factor preview, not full classical Shadbala. It compares Sthana, Dig, Chesta, and Naisargika Bala; Kala and Drik Bala are not included in the score.",
    offerTitle: "Read these strengths in your complete Kundali",
    offerBody:
      "A strength score alone cannot diagnose a life problem. The full report adds houses, dashas, divisional charts, doshas, and chart-specific context before drawing conclusions or suggesting upay.",
    offerCta: "Get complete Kundali report",
  },
  hi: {
    heading: "ग्रह बल प्रीव्यू — षड्बल के 4 गणना किए गए कारक",
    submit: "ग्रह बल की गणना करें",
    summary: "गणना किया गया बल प्रीव्यू",
    strongest: "सबसे ऊँचा स्कोर",
    lowest: "सबसे कम स्कोर",
    methodology:
      "यह 4 कारकों वाला खुला प्रीव्यू है, पूरा शास्त्रीय षड्बल नहीं। स्कोर में स्थान, दिग्, चेष्टा और नैसर्गिक बल हैं; काल और दृक् बल अभी शामिल नहीं हैं।",
    offerTitle: "इन बलों को अपनी पूरी कुंडली में समझें",
    offerBody:
      "सिर्फ़ बल के स्कोर से जीवन की समस्या का निदान नहीं होता। पूरी रिपोर्ट निष्कर्ष या उपाय से पहले भाव, दशाएँ, वर्ग चार्ट, दोष और आपकी कुंडली का संदर्भ जोड़ती है।",
    offerCta: "पूरी कुंडली रिपोर्ट पाएँ",
  },
} as const;

const STRENGTH_BADGE_CLASS: Record<string, string> = {
  strong: "bg-[var(--color-success-text-dark)]",
  moderate: "bg-[var(--ish-accent)]",
  weak: "bg-[var(--color-error-text-dark)]",
};

export default function ShadbalaClient({ language = "en" }: { language?: Language }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();
  const [result, setResult] = useState<ShadbalaResponse | null>(null);
  const [submittedBirth, setSubmittedBirth] = useState<ShadbalaRequest | null>(null);
  const resultRef = useRef<HTMLDivElement>(null);
  const copy = COPY[language];

  async function handleSubmit(data: KundaliRequest) {
    setError(undefined);
    setLoading(true);
    setResult(null);
    try {
      const request = toShadbalaRequest(data);
      const response = await calculateShadbala(request);
      setSubmittedBirth(request);
      setResult(response);
      trackCalculatorUsed({
        calculator: "shadbala",
        language,
        source_path: sourcePath(language),
        has_time: true,
      });
      setTimeout(
        () => resultRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }),
        100
      );
    } catch (submitError: unknown) {
      setError(
        submitError instanceof Error
          ? submitError.message
          : "Something went wrong. Please try again."
      );
    } finally {
      setLoading(false);
    }
  }

  return (
    <section className="mb-10">
      <BirthDetailsForm
        onSubmit={handleSubmit}
        loading={loading}
        error={error}
        heading={copy.heading}
        headingLevel="h2"
        submitLabel={copy.submit}
        requireBirthTime
        requireResolvedPlace
        collectName={false}
        language={language}
        analyticsToolName="shadbala"
        analyticsPlacement="shadbala_birth_details"
        rememberBirthDetails
        showTrustStrip
      />
      {result && submittedBirth ? (
        <ShadbalaResults
          result={result}
          request={submittedBirth}
          resultRef={resultRef}
          language={language}
        />
      ) : null}
    </section>
  );
}

function toShadbalaRequest(data: KundaliRequest): ShadbalaRequest {
  if (!data.birth_time) throw new Error("Exact birth time is required for directional strength.");
  if (data.latitude == null || data.longitude == null) {
    throw new Error("Select a birth city from the suggestions to calculate its coordinates.");
  }
  return {
    birth_date: data.birth_date,
    birth_time: data.birth_time,
    birth_place: data.birth_place,
    latitude: data.latitude,
    longitude: data.longitude,
    timezone: data.timezone,
  };
}

function ShadbalaResults({
  result,
  request,
  resultRef,
  language,
}: {
  result: ShadbalaResponse;
  request: ShadbalaRequest;
  resultRef: RefObject<HTMLDivElement | null>;
  language: Language;
}) {
  const copy = COPY[language];
  return (
    <div ref={resultRef} className="card-sacred mt-8 rounded-2xl p-6">
      <ShadbalaSummary result={result} language={language} />
      <ShadbalaTable planets={result.planet_shadbala_table} />
      <p className="mt-5 rounded-lg border border-[var(--color-warning-tint)] bg-[var(--color-warning-bg)] p-3 text-body-small leading-5 text-[var(--color-warning-text)]">
        {copy.methodology}
      </p>
      <OfferPlacement
        {...pageReportBridgeOffer({
          source: sourcePath(language),
          placement: offerPlacement(language),
          language: language,
          birthContext: {
            birthDate: request.birth_date,
            birthTime: request.birth_time,
            birthPlace: request.birth_place,
          },
        })}
        headline={copy.offerTitle}
        subhead={copy.offerBody}
        ctaLabel={copy.offerCta}
      />
      <p className="mt-6 text-[11px] text-ish-text-muted">
        {result.note} · Sidereal zodiac (Lahiri ayanamsa) · Ishvaram Jyotish engine
      </p>
    </div>
  );
}

function ShadbalaSummary({ result, language }: { result: ShadbalaResponse; language: Language }) {
  const copy = COPY[language];
  return (
    <div className="mb-4">
      <p className="text-caption font-semibold uppercase tracking-wide text-intent-brand">
        {copy.summary} · {result.birth_place} · {result.birth_date}
      </p>
      <h3 className="mt-1 font-serif text-[20px] font-bold text-ish-dark">
        {copy.strongest}:{" "}
        <span className="text-intent-brand">{planetName(result, result.strongest_planet)}</span>
      </h3>
      <p className="mt-1 text-body-small text-ish-text-muted">
        {copy.lowest}:{" "}
        <strong className="text-ish-dark">{planetName(result, result.weakest_planet)}</strong>
      </p>
    </div>
  );
}

function ShadbalaTable({ planets }: { planets: PlanetShadbalaRow[] }) {
  return (
    <div className="mt-6 overflow-x-auto">
      <table className="w-full border-collapse text-caption">
        <thead>
          <tr className="border-b border-[var(--ish-border)] text-left text-[11px] font-semibold uppercase tracking-wide text-intent-brand">
            <th className="py-2 pr-3">Planet</th>
            <th className="py-2 pr-2 text-right">Sthana</th>
            <th className="py-2 pr-2 text-right">Dig</th>
            <th className="py-2 pr-2 text-right">Chesta</th>
            <th className="py-2 pr-2 text-right">Naisarg.</th>
            <th className="py-2 pr-2 text-right">Score</th>
            <th className="py-2">Strength</th>
          </tr>
        </thead>
        <tbody>
          {planets.map((planet) => (
            <tr key={planet.planet_id} className="border-b border-[var(--ish-border)]">
              <td className="py-2 pr-3 text-ish-dark">
                <span className="font-semibold">{planet.planet_display_en}</span>
                <span className="ml-1 text-[11px] text-ish-text-muted">
                  {planet.planet_display_hi}
                </span>
              </td>
              <ScoreCell value={planet.sthana_bala} />
              <ScoreCell value={planet.dig_bala} />
              <ScoreCell value={planet.chesta_bala} />
              <ScoreCell value={planet.naisargika_bala} />
              <td className="py-2 pr-2 text-right font-semibold text-ish-dark">
                {planet.total_score}
              </td>
              <td className="py-2">
                <StrengthBadge strength={planet.strength} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="mt-2 text-[10px] text-ish-text-muted">
        Sthana = positional · Dig = directional · Chesta = motional · Naisarg. = natural · Score =
        normalized 4-factor comparison
      </p>
    </div>
  );
}

function ScoreCell({ value }: { value: number }) {
  return <td className="py-2 pr-2 text-right text-ish-text-muted">{value}</td>;
}

function StrengthBadge({ strength }: { strength: string }) {
  const background = STRENGTH_BADGE_CLASS[strength] ?? STRENGTH_BADGE_CLASS.moderate;
  return (
    <span
      className={`${background} inline-block rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white`}
    >
      {strength}
    </span>
  );
}

function sourcePath(language: Language) {
  return language === "hi" ? "/hi/shadbala" : "/shadbala";
}

function offerPlacement(language: Language) {
  return language === "hi"
    ? RUNTIME_OFFER_PLACEMENT_HI_SHADBALA_REPORT_BRIDGE
    : RUNTIME_OFFER_PLACEMENT_SHADBALA_REPORT_BRIDGE;
}

function planetName(result: ShadbalaResponse, planetId: string) {
  return (
    result.planet_shadbala_table.find((planet) => planet.planet_id === planetId)
      ?.planet_display_en ?? planetId
  );
}
